import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { KeycloakService } from 'keycloak-angular';
import { ConfigService } from 'src/app/services/config.service';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { IdentityService } from 'src/app/services/identity.service';
import { MenuService } from 'src/app/services/menu.service';
import { SettingsService } from 'src/app/services/settings.service';
import { TeamsService } from 'src/app/services/teams.service';
import { VersionService } from 'src/app/services/version.service';
import { WidgetService } from 'src/app/services/widget.service';
import { DynamicWidgetsService, DynamicWidget } from 'src/app/services/dynamic-widgets.service';
import { UserPrefsService } from 'src/app/services/user-prefs.service';
import { MuxSessionService, parsePlaceAddress } from 'src/app/services/mux-session.service';

const CHANGELOG_URL = 'https://docs.lit.ai/reference/changelog/';
const PINNED_APPS_KEY = 'lit-pinned-apps';
const PINNED_SKILLS_KEY = 'lit-pinned-skills';
const PINNED_DYNAMIC_KEY = 'lit-pinned-dynamic-widgets';
const PINNED_THEME_KEY = 'lit-pinned-theme';
const PINNED_LAYOUT_KEY = 'lit-pinned-layout';
const PINNED_PLACES_KEY = 'lit-pinned-places';
const PINNED_LOGOUT_KEY = 'lit-pinned-logout';

interface AppItem {
  name: string;
  widget: WidgetService.WidgetName;
  icon: string;
  category?: string;
  visible?: boolean;
  /** Extra inputs passed directly to the widget, bypassing the named-widget registry. */
  inputs?: Record<string, any>;
  /** Semantic role — e.g. 'chat' marks the primary chat app for the Settings "Start Chat" button. */
  role?: string;
}

interface SkillPack {
  name: string;
  icon: string;
  apps: AppItem[];
}

/** A row in the command palette (Ctrl+K). Everything the Apps menu offers,
 *  plus team switching, findable by typing. Built fresh on every open. */
interface PaletteCommand {
  id: string;
  label: string;
  icon: string;
  category: string;
  run: () => void;
  /** Present when the row can be pinned to the menubar rail. */
  pinned?: () => boolean;
  togglePin?: (event: MouseEvent) => void;
  /** Marks the current selection (e.g. the active team). */
  active?: boolean;
  /** Keeps the palette open after running (e.g. drill-into-pack rows). */
  keepOpen?: boolean;
}

type PaletteRow = PaletteCommand & { flatIndex: number };

@Component({
    selector: 'app-menubar',
    templateUrl: './menubar.component.html',
    styleUrls: ['./menubar.component.scss'],
    standalone: false
})
export class MenubarComponent implements OnInit, OnDestroy {
  private subs: { unsubscribe(): void }[] = [];
  @Output() openWidget = new EventEmitter<WidgetService.WidgetName>();
  @Output() openWidgetForTeam = new EventEmitter<{ name: string; team: string; title?: string }>();
  @Output() openDynamicWidget = new EventEmitter<DynamicWidget>();
  @Output() openMarkdownViewer = new EventEmitter<{ path: string; title: string }>();
  /** Fired when an AppItem carries `inputs` — routes directly to ExternalAppWidget. */
  @Output() openExternalApp = new EventEmitter<Record<string, any>>();
  /** Opens a Chat widget scoped to a specific team (palette "Open chat: …"). */
  // Chat-open requests route through WidgetService.openChatForTeam$ (see
  // its doc comment) — an @Output here only reaches the host that binds it,
  // and the visible menubar instance isn't always that host.
  @ViewChild('launcherTrigger') launcherTrigger: MatMenuTrigger;

  @HostBinding('class.menubar-top')
  private get _menubarTop() {
    return this.menubarPosition === 'top';
  }

  @HostBinding('class.menubar-left')
  private get _menubarLeft() {
    return this.menubarPosition === 'left';
  }

  @HostBinding('class.menubar-right')
  private get _menubarRight() {
    return this.menubarPosition === 'right';
  }

  @HostBinding('class.menubar-bottom')
  private get _menubarBottom() {
    return this.menubarPosition === 'bottom';
  }

  // Toolbar opacity is now handled via CSS variable (--toolbar-opacity) in the stylesheet

  public version: string | undefined;
  public chatEnabled: boolean = true;
  public myTeams: { id: string; name: string; slug: string; members: string[] }[] = [];
  public favoriteTeams: Set<string> = new Set();

  private static readonly FAVORITE_TEAMS_KEY = 'lit-favorite-teams';

  /** Teams to show in quick-switch menu */
  public get visibleTeams(): { id: string; name: string; slug: string; members: string[] }[] {
    return this.myTeams;
  }

  /** Display name of the currently selected team */
  public get currentTeamName(): string {
    const slug = this.identity.me?.selectedTeamName;
    const match = this.myTeams.find(t => (t.slug || t.name) === slug);
    return match?.name || slug || 'Team';
  }

  private _appFilter: string = '';
  public get appFilter(): string { return this._appFilter; }
  public set appFilter(value: string) {
    this._appFilter = value;
    this.updateFilteredApps();
    this._cdr.detectChanges();
  }

  /** True when the vendor has provided a full menu via whiteLabel.menu in env.js. */
  public get useCustomMenu(): boolean {
    return Array.isArray(window['env']?.whiteLabel?.menu);
  }

  /** Returns vendor's custom menu items, or null if not configured. */
  public get customMenuApps(): AppItem[] {
    return (window['env']?.whiteLabel?.menu as AppItem[]) ?? [];
  }

  public get isKnowledgeGraphEnabled(): boolean {
    return this._settings.labsKnowledgeGraph;
  }

  // Core workspace apps - always available (used when whiteLabel.menu is not set)
  // Note: 'MarkdownWidget' is special-cased to prompt for path
  public readonly workspaceApps: AppItem[] = [
    { name: 'Workspace', widget: 'HeartbeatWidget', icon: 'chat', category: 'Workspace' },
    { name: 'Work Sessions', widget: 'ChatWidget', icon: 'tag', category: 'Workspace' },
    { name: 'Files', widget: 'FilesWidget', icon: 'folder', category: 'Workspace' },
    { name: 'Terminal', widget: 'TerminalWidget', icon: 'terminal', category: 'Workspace', visible: true },
    { name: 'Browser', widget: 'BrowserWidget', icon: 'language', category: 'Workspace' },
    { name: 'Markdown Viewer', widget: 'MarkdownWidget' as WidgetService.WidgetName, icon: 'description', category: 'Workspace' },
    { name: 'Settings', widget: 'SettingsWidget', icon: 'settings', category: 'Workspace' },
  ];

  // Default skill packs - full LIT platform capabilities
  private readonly _defaultSkillPacks: SkillPack[] = [
    {
      name: 'Deep Learning',
      icon: 'psychology',
      apps: [
        { name: 'Datasource Creator', widget: 'RawDataWidget', icon: 'storage', category: 'Deep Learning' },
        { name: 'Datasource Explorer', widget: 'FeatureSelectorWidget', icon: 'explore', category: 'Deep Learning' },
        { name: 'Feature Lab', widget: 'FeatureEditorWidget', icon: 'science', category: 'Deep Learning' },
        { name: 'Dataset Builder', widget: 'BuildsWidget', icon: 'handyman', category: 'Deep Learning' },
        { name: 'Dataset QA', widget: 'AssetsWidget', icon: 'network_check', category: 'Deep Learning' },
        { name: 'Model Designer', widget: 'DefinitionsWidget', icon: 'account_tree', category: 'Deep Learning' },
        { name: 'Model Arena', widget: 'ExperimentsWidget', icon: 'model_training', category: 'Deep Learning' },
        { name: 'Model Vault', widget: 'VaultWidget', icon: 'key', category: 'Deep Learning' },
        { name: 'Playground', widget: 'PlaygroundWidget', icon: 'science', category: 'Deep Learning' },
        { name: 'Deployments', widget: 'DeploysWidget', icon: 'rocket_launch', category: 'Deep Learning' },
      ]
    },
    {
      name: 'Services',
      icon: 'dns',
      apps: [
        { name: 'Streams', widget: 'StreamsWidget', icon: 'sensors', category: 'Services' },
        { name: 'Jobs', widget: 'JobsWidget', icon: 'schedule', category: 'Services' },
      ]
    },
    {
      name: 'Trading',
      icon: 'candlestick_chart',
      apps: [
        { name: 'Live Chart', widget: 'StreamWidget', icon: 'candlestick_chart', category: 'Trading' },
      ]
    },
  ];

  // Skill packs - configured via whiteLabel.skillPacks in env.js
  // null = show all (default), [] = hide all, or specific array
  public get skillPacks(): SkillPack[] {
    const configured = window['env']?.whiteLabel?.skillPacks;
    // If explicitly configured (including empty array), use that
    if (configured !== undefined && configured !== null) {
      return configured;
    }
    // Default: show all skill packs
    return this._defaultSkillPacks;
  }

  public readonly systemApps: AppItem[] = [];

  // Dynamic widgets loaded from ~/.config/lit/widgets/ and /etc/lit/widgets/
  public dynamicWidgets: DynamicWidget[] = [];

  // Dynamic widgets grouped by first path segment for nested menus
  // This is a stored property (not a getter) so Angular Material can properly bind menus
  public dynamicWidgetGroups: { name: string; icon: string; widgets: DynamicWidget[] }[] = [];

  // Widgets at root level (single-segment path)
  public rootDynamicWidgets: DynamicWidget[] = [];

  private updateDynamicWidgetGroups(): void {
    const groups = new Map<string, DynamicWidget[]>();

    for (const widget of this.dynamicWidgets) {
      // If path has multiple segments, group by first segment
      // Otherwise put in 'Team Apps' group
      const groupName = widget.path.length > 1 ? widget.path[0] : 'Team Apps';
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(widget);
    }

    this.dynamicWidgetGroups = Array.from(groups.entries()).map(([name, widgets]) => ({
      name,
      icon: 'folder',
      widgets
    }));

    this.rootDynamicWidgets = this.dynamicWidgets.filter(w => w.path.length === 1);
  }

  // Combined list for search/filtering
  public get allApps(): AppItem[] {
    if (this.useCustomMenu) {
      return [...this.customMenuApps, ...this.systemApps];
    }
    const skillApps = this.skillPacks.flatMap(pack => pack.apps);
    return [...this.workspaceApps, ...skillApps, ...this.systemApps];
  }

  // Cached filtered results - only recomputed when filter changes
  public groupedApps: { category: string; apps: AppItem[] }[] = [];

  // Pinned apps
  public pinnedWidgets: Set<WidgetService.WidgetName> = new Set();

  // Pinned skill packs
  public pinnedSkillNames: Set<string> = new Set();

  // Pinned dynamic widgets
  public pinnedDynamicIds: Set<string> = new Set();

  // Pinned theme toggle
  public isThemePinned = true;

  // Pinned layout toggle
  public isLayoutPinned = false;

  public get pinnedApps(): AppItem[] {
    return this.allApps.filter(app => this.pinnedWidgets.has(app.widget));
  }

  public get pinnedSkillPacks(): SkillPack[] {
    return this.skillPacks.filter(pack => this.pinnedSkillNames.has(pack.name));
  }

  public isPinned(widget: WidgetService.WidgetName): boolean {
    return this.pinnedWidgets.has(widget);
  }

  public isSkillPackPinned(name: string): boolean {
    return this.pinnedSkillNames.has(name);
  }

  public togglePin(app: AppItem, event: MouseEvent): void {
    event.stopPropagation(); // Don't trigger the menu item click
    if (this.pinnedWidgets.has(app.widget)) {
      this.pinnedWidgets.delete(app.widget);
    } else {
      this.pinnedWidgets.add(app.widget);
    }
    this.savePinnedApps();
  }

  public toggleSkillPackPin(pack: SkillPack, event: MouseEvent): void {
    event.stopPropagation();
    if (this.pinnedSkillNames.has(pack.name)) {
      this.pinnedSkillNames.delete(pack.name);
    } else {
      this.pinnedSkillNames.add(pack.name);
    }
    this.savePinnedSkills();
  }

  private loadPinnedApps(): void {
    try {
      const stored = localStorage.getItem(PINNED_APPS_KEY);
      if (stored) {
        const widgets = JSON.parse(stored) as WidgetService.WidgetName[];
        this.pinnedWidgets = new Set(widgets);
      } else {
        // Default: Chat is pinned
        this.pinnedWidgets = new Set(['HeartbeatWidget'] as WidgetService.WidgetName[]);
      }
    } catch (e) {
      console.warn('Failed to load pinned apps:', e);
      this.pinnedWidgets = new Set(['HeartbeatWidget'] as WidgetService.WidgetName[]);
    }
  }

  private loadPinnedSkills(): void {
    try {
      const stored = localStorage.getItem(PINNED_SKILLS_KEY);
      if (stored) {
        const skills = JSON.parse(stored) as string[];
        this.pinnedSkillNames = new Set(skills);
      }
    } catch (e) {
      console.warn('Failed to load pinned skills:', e);
    }
  }

  private savePinnedApps(): void {
    try {
      const widgets = Array.from(this.pinnedWidgets);
      localStorage.setItem(PINNED_APPS_KEY, JSON.stringify(widgets));
    } catch (e) {
      console.warn('Failed to save pinned apps:', e);
    }
  }

  private savePinnedSkills(): void {
    try {
      const skills = Array.from(this.pinnedSkillNames);
      localStorage.setItem(PINNED_SKILLS_KEY, JSON.stringify(skills));
    } catch (e) {
      console.warn('Failed to save pinned skills:', e);
    }
  }

  /** User pins plus every `surface: panel` app — those are permanent rail
   *  entries (the system-app contract), not favourites. */
  public get pinnedDynamicWidgets(): DynamicWidget[] {
    return this.dynamicWidgets.filter(w => w.surface === 'panel' || this.pinnedDynamicIds.has(w.id));
  }

  /** host.badge.set count for a rail pin ('' when none). */
  public appBadge(dw: DynamicWidget): string {
    const n = this._dynamicWidgets.badgeFor(dw.name);
    return n > 0 ? DynamicWidgetsService.badgeLabel(n) : '';
  }

  public isDynamicPinned(id: string): boolean {
    return this.pinnedDynamicIds.has(id);
  }

  public toggleDynamicPin(widget: DynamicWidget, event: MouseEvent): void {
    event.stopPropagation();
    if (this.pinnedDynamicIds.has(widget.id)) {
      this.pinnedDynamicIds.delete(widget.id);
    } else {
      this.pinnedDynamicIds.add(widget.id);
    }
    this.savePinnedDynamic();
  }

  private loadPinnedDynamic(): void {
    try {
      const stored = localStorage.getItem(PINNED_DYNAMIC_KEY);
      if (stored) {
        this.pinnedDynamicIds = new Set(JSON.parse(stored) as string[]);
      }
    } catch (e) {
      console.warn('Failed to load pinned dynamic widgets:', e);
    }
  }

  private savePinnedDynamic(): void {
    try {
      localStorage.setItem(PINNED_DYNAMIC_KEY, JSON.stringify(Array.from(this.pinnedDynamicIds)));
    } catch (e) {
      console.warn('Failed to save pinned dynamic widgets:', e);
    }
  }

  public toggleThemePin(event: MouseEvent): void {
    event.stopPropagation();
    this.isThemePinned = !this.isThemePinned;
    localStorage.setItem(PINNED_THEME_KEY, String(this.isThemePinned));
  }

  public toggleLayoutPin(event: MouseEvent): void {
    event.stopPropagation();
    this.isLayoutPinned = !this.isLayoutPinned;
    localStorage.setItem(PINNED_LAYOUT_KEY, String(this.isLayoutPinned));
  }

  public loadFavoriteTeams(): void {
    try {
      const stored = localStorage.getItem(MenubarComponent.FAVORITE_TEAMS_KEY);
      if (stored) {
        const teams = JSON.parse(stored) as string[];
        this.favoriteTeams = new Set(teams);
      }
    } catch (e) {
      console.warn('Failed to load favorite teams:', e);
    }
  }

  public saveFavoriteTeams(): void {
    try {
      const teams = Array.from(this.favoriteTeams);
      localStorage.setItem(MenubarComponent.FAVORITE_TEAMS_KEY, JSON.stringify(teams));
    } catch (e) {
      console.warn('Failed to save favorite teams:', e);
    }
  }

  public toggleFavoriteTeam(teamName: string): void {
    if (this.favoriteTeams.has(teamName)) {
      this.favoriteTeams.delete(teamName);
    } else {
      this.favoriteTeams.add(teamName);
    }
    this.saveFavoriteTeams();
  }

  public isTeamFavorite(teamName: string): boolean {
    return this.favoriteTeams.has(teamName);
  }

  private updateFilteredApps(): void {
    let filtered: AppItem[];
    if (!this._appFilter.trim()) {
      filtered = this.allApps;
    } else {
      const filter = this._appFilter.toLowerCase();
      filtered = this.allApps.filter(app =>
        app.name.toLowerCase().includes(filter) ||
        (app.category ?? '').toLowerCase().includes(filter)
      );
    }

    // Group by category
    const groups = new Map<string, AppItem[]>();
    for (const app of filtered) {
      const cat = app.category ?? 'Apps';
      if (!groups.has(cat)) {
        groups.set(cat, []);
      }
      groups.get(cat)!.push(app);
    }
    this.groupedApps = Array.from(groups.entries()).map(([category, apps]) => ({ category, apps }));
  }

  public get menubarPosition() {
    return this._settings.menubarPosition;
  }

  /** Tooltip direction pointing away from the menubar edge */
  public get tooltipAway(): 'above' | 'below' | 'left' | 'right' {
    switch (this.menubarPosition) {
      case 'left': return 'right';
      case 'right': return 'left';
      case 'top': return 'below';
      case 'bottom': return 'above';
      default: return 'right';
    }
  }

  public get logoUrl(): string | null {
    const wl = window['env']?.whiteLabel;
    const isVertical = this.menubarPosition === 'left' || this.menubarPosition === 'right';
    if (isVertical) {
      return wl?.logoVerticalUrl || null;
    }
    return wl?.logoUrl || 'assets/images/lit-logo.png';
  }

  public get changelogURL(): string {
    if (this.version !== undefined) {
      return `${CHANGELOG_URL}#${this.version}`;
    }
    return CHANGELOG_URL;
  }

  public get sshExecutionEnabled(): boolean {
    return ConfigService.sshExecutionEnabled;
  }

  public get localMode(): boolean {
    return ConfigService.localMode;
  }


  constructor(
    private readonly _router: Router,
    private readonly _menu: MenuService,
    private readonly _version: VersionService,
    private readonly _settings: SettingsService,
    private readonly _teams: TeamsService,
    private readonly _config: ConfigService,
    public identity: IdentityService,
    private readonly _keycloak: KeycloakService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _dynamicWidgets: DynamicWidgetsService,
    private readonly _userPrefs: UserPrefsService,
    private readonly _muxSession: MuxSessionService,
    private readonly _widgetSvc: WidgetService,
    private readonly _http: HttpClient
  ) {
    (<any>window).menubar = this;
  }

  private _remotePlacesTimer: any = null;

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    if (this._remotePlacesTimer) {
      clearInterval(this._remotePlacesTimer);
      this._remotePlacesTimer = null;
    }
  }

  public ngOnInit(): void {
    this._version.get().then(({ version }) => {
      this.version = version;
    });

    this.subs.push(this._teams.collection$.subscribe((teams) => {
      this.myTeams = teams;
    }));
    this.subs.push(this._dynamicWidgets.badges$.subscribe(() => this._cdr.markForCheck()));
    this._teams.load();
    this.loadTeamRole();

    // Initialize grouped apps and load pinned apps/skills/teams
    this.loadPinnedApps();
    this.loadPinnedSkills();
    this.loadPinnedDynamic();
    const storedThemePin = localStorage.getItem(PINNED_THEME_KEY);
    this.isThemePinned = storedThemePin === null ? true : storedThemePin === 'true';
    this.isLayoutPinned = localStorage.getItem(PINNED_LAYOUT_KEY) === 'true';
    this.isLogoutPinned = localStorage.getItem(PINNED_LOGOUT_KEY) === 'true';
    this.loadPinnedPlaces();
    this.loadFavoriteTeams();
    this.updateFilteredApps();
    // Rail state for starred remote places (grey-when-offline) — refreshed
    // here, on every palette open, and by a conditional poll that only costs
    // anything while a remote pin actually exists (otherwise it's a no-op
    // tick). Event-push over the telemetry WS is the successor if presence
    // ever needs to be snappier than 30s.
    void this.refreshRemotePlaces();
    this._remotePlacesTimer = setInterval(() => {
      const hasRemotePin = Array.from(this.pinnedPlaceSlugs).some(s => !!parsePlaceAddress(s));
      if (hasRemotePin) void this.refreshRemotePlaces();
    }, 30000);

    // Load dynamic widgets
    this.loadDynamicWidgets();
  }

  /** Whether the signed-in user may administer the selected team's policy
   *  (Keycloak own-/adm-{team} realm role, or named in policy.json). Gates the
   *  "Team Policy" palette entry; the widget re-checks server-side. */
  public teamAdmin = false;
  private async loadTeamRole(): Promise<void> {
    if (ConfigService.localMode) return;
    const team = this.identity.me?.selectedTeamName;
    if (!team) return;
    try {
      const me = await lastValueFrom(this._http.get<any>(`${ConfigService.muxUrl}/teams/${encodeURIComponent(team)}/policy/me`));
      this.teamAdmin = me?.role === 'owner' || me?.role === 'admin';
    } catch { this.teamAdmin = false; }
    this._paletteCommands = this.buildPaletteCommands();
    this._cdr.markForCheck();
  }

  private async loadDynamicWidgets(): Promise<void> {
    try {
      const team = this.identity.me?.selectedTeamName;
      this.dynamicWidgets = await this._dynamicWidgets.getWidgets(team);
      this.updateDynamicWidgetGroups();
      console.log('Loaded dynamic widgets for team', team, ':', this.dynamicWidgets);
      console.log('Dynamic widget groups:', this.dynamicWidgetGroups);
      this._cdr.detectChanges();
    } catch (error) {
      console.error('Failed to load dynamic widgets:', error);
    }
  }

  public navigate(route: string[]): void {
    this._menu.setActive(route[route.length - 1]);
    this._router.navigate(route);
  }

  public toggleTheme(): void {
    this._settings.theme = this._settings.theme === 'dark' ? 'light' : 'dark';
  }

  public get isDarkTheme(): boolean {
    return this._settings.theme === 'dark';
  }

  public get isDockviewMode(): boolean {
    return this._settings.windowManagerType === 'dockview';
  }

  public get isLayoutLocked(): boolean {
    return !!window['env']?.whiteLabel?.forceLayout;
  }

  public toggleLayoutEngine(): void {
    this._settings.windowManagerType = this.isDockviewMode ? 'gridster' : 'dockview';
    window.location.reload();
  }

  public openUserManager(): void {
    const url = `${ConfigService.keycloakUrl}/admin/master/console/#/realms/${ConfigService.keycloakRealm}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  selectTeam(element) {
    if (element.name === '[New Team]') {
      this._router.navigate(['/']);
      return;
    }

    const slug = element.slug || element.name;
    if (this.identity.me.selectedTeamName === slug) return;

    localStorage.setItem('lastTeamSelected', slug);
    this.identity.setTeam(slug);
    this.identity.setProject(null);
    this.loadTeamRole();

    // Save preference server-side for cross-device sync
    this._userPrefs.setPreferredTeam(slug).subscribe();

    this._router.navigate([`/${slug}/grid`]);
  }

  logout() {
    // Debug: inspect keycloak state before logout
    const kc = (this._keycloak as any)._instance;
    console.log('=== LOGOUT DEBUG ===');
    console.log('KeycloakService:', this._keycloak);
    console.log('Keycloak instance (_instance):', kc);
    console.log('authServerUrl:', kc?.authServerUrl);
    console.log('realm:', kc?.realm);
    console.log('clientId:', kc?.clientId);
    console.log('endpoints:', kc?.endpoints);
    if (kc?.endpoints?.logout) {
      console.log('logout endpoint:', kc.endpoints.logout());
    }
    this._keycloak.logout();
  }

  launchApp(appOrWidget: AppItem | WidgetService.WidgetName) {
    const widget = typeof appOrWidget === 'string' ? appOrWidget : appOrWidget.widget;
    const inputs = typeof appOrWidget === 'string' ? undefined : (appOrWidget as AppItem).inputs;

    // Special case: MarkdownWidget opens blank in edit mode
    if (widget === 'MarkdownWidget') {
      this.openMarkdownViewer.emit({ path: '', title: 'New Document', startInEditMode: true } as any);
      this.appFilter = '';
      this.launcherTrigger?.closeMenu();
      return;
    }

    if (inputs && Object.keys(inputs).length > 0) {
      this.openExternalApp.emit(inputs);
    } else {
      this.openWidget.emit(widget);
    }
    this.appFilter = '';
    this.launcherTrigger?.closeMenu();
  }

  launchDynamicWidget(widget: DynamicWidget) {
    this.openDynamicWidget.emit(widget);
    this.appFilter = '';
    this.launcherTrigger?.closeMenu();
  }

  /** Launch Knowledge Graph as a first-class widget */
  launchKnowledgeGraph() {
    if (!this.isKnowledgeGraphEnabled) return;
    this.launchApp('KnowledgeGraphWidget');
  }

  onLauncherClosed() {
    this.appFilter = '';
  }

  onSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.appFilter) {
        this.appFilter = '';
        event.stopPropagation(); // Don't close menu, just clear filter
      }
      // If filter is already empty, let Escape close the menu naturally
    } else {
      event.stopPropagation(); // Prevent other keys from triggering menu navigation
    }
  }

  // --- Command palette (Ctrl+K) ---

  @ViewChild('paletteInput') paletteInput: ElementRef<HTMLInputElement>;

  public paletteOpen = false;
  public paletteQuery = '';
  public paletteIndex = 0;
  public paletteGroups: { category: string; commands: PaletteRow[] }[] = [];
  private _paletteCommands: PaletteCommand[] = [];
  private _paletteFlat: PaletteRow[] = [];

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    const mod = event.ctrlKey || event.metaKey;
    if (mod && !event.shiftKey && !event.altKey && key === 'k') {
      event.preventDefault();
      this.paletteOpen ? this.closePalette() : this.openPalette();
    } else if (mod && event.shiftKey && key === 'p') {
      // VS Code muscle memory, where the browser doesn't claim it.
      event.preventDefault();
      this.openPalette();
    }
  }

  public openPalette(): void {
    this._paletteCommands = this.buildPaletteCommands();
    this.paletteQuery = '';
    this.paletteIndex = 0;
    this.paletteOpen = true;
    this.updatePaletteFilter();
    // This component's change detection needs a manual crank (see the
    // appFilter setter) — without it the overlay waits for an unrelated
    // event to render. Detect synchronously, then focus the now-real input.
    this._cdr.detectChanges();
    this.paletteInput?.nativeElement.focus();
    // Remote places are live-only — refresh the device list every open; rows
    // appear (and rail pins un-grey) when the fetch lands.
    void this.refreshRemotePlaces();
  }

  private remoteDevices: MuxSessionService.RemoteDevice[] = [];
  private remoteDeviceTeams = new Map<string, { slug: string; name: string }[]>();

  private async refreshRemotePlaces(): Promise<void> {
    const devices = await this._muxSession.listRemoteDevices();
    this.remoteDevices = devices;
    // The registry only knows org ids; the pipe's teams.list has display
    // names and slugs (the token the desktop's endpoints expect).
    await Promise.all(devices.filter(d => d.online).map(async d => {
      const teams = await this._muxSession.listRemoteDeviceTeams(d.device_id);
      if (teams.length) this.remoteDeviceTeams.set(d.device_id, teams);
    }));
    if (this.paletteOpen) {
      this._paletteCommands = this.buildPaletteCommands();
      this.updatePaletteFilter();
    }
    this._cdr.detectChanges();
  }

  public closePalette(): void {
    this.paletteOpen = false;
    this._cdr.detectChanges();
  }

  public setPaletteIndex(index: number): void {
    this.paletteIndex = index;
    this._cdr.detectChanges();
  }

  /** Team-scoped apps openable AT AN ADDRESS from the palette. One palette
   *  entry each; selecting one re-lists your teams (two-step — nothing
   *  combinatorial). Venue rides the panel (scope taxonomy, 2026-09-01). */
  private static TEAM_APPS: { name: string; title: string; icon: string }[] = [
    { name: 'AgentsWidget', title: 'Agents', icon: 'group' },
    { name: 'DefinitionsWidget', title: 'Definitions', icon: 'schema' },
    { name: 'SchemasWidget', title: 'Schemas', icon: 'account_tree' },
    { name: 'BuildsWidget', title: 'Builds', icon: 'construction' },
    { name: 'ExperimentsWidget', title: 'Experiments', icon: 'science' },
    { name: 'StreamsWidget', title: 'Streams', icon: 'stacked_line_chart' },
    { name: 'RawDataWidget', title: 'Raw Data', icon: 'dataset' },
    { name: 'LabWidget', title: 'Lab', icon: 'biotech' },
    { name: 'AssetsWidget', title: 'Assets', icon: 'inventory_2' },
    { name: 'JobsWidget', title: 'Jobs', icon: 'schedule' },
  ];

  private buildPaletteCommands(): PaletteCommand[] {
    const cmds: PaletteCommand[] = [];

    // Everything the Apps menu offers (workspace + skill packs, or the
    // white-label custom menu), with the same pin-to-toolbar affordance.
    for (const app of this.allApps) {
      cmds.push({
        id: `app-${app.widget}-${app.name}`,
        label: app.name,
        icon: app.icon,
        category: app.category ?? 'Apps',
        run: () => this.launchApp(app),
        pinned: () => this.isPinned(app.widget),
        togglePin: (e) => this.togglePin(app, e),
      });
    }

    if (this.isKnowledgeGraphEnabled && !this.useCustomMenu) {
      cmds.push({
        id: 'app-KnowledgeGraphWidget',
        label: 'Knowledge Graph',
        icon: 'hub',
        category: 'Workspace',
        run: () => this.launchKnowledgeGraph(),
        pinned: () => this.isPinned('KnowledgeGraphWidget'),
        togglePin: (e) => this.togglePin({ name: 'Knowledge Graph', widget: 'KnowledgeGraphWidget', icon: 'hub' } as AppItem, e),
      });
    }

    // Skill packs as drill-in rows: running one filters the palette to the
    // pack's apps; the star pins the whole pack to the rail as a dropdown.
    if (!this.useCustomMenu) {
      for (const pack of this.skillPacks) {
        cmds.push({
          id: `pack-${pack.name}`,
          label: pack.name,
          icon: pack.icon,
          category: 'Skill Packs',
          keepOpen: true,
          run: () => {
            this.paletteQuery = pack.name;
            this.updatePaletteFilter();
            this.paletteInput?.nativeElement.focus();
          },
          pinned: () => this.isSkillPackPinned(pack.name),
          togglePin: (e) => this.toggleSkillPackPin(pack, e),
        });
      }
    }

    // Team Apps (dynamic widgets), grouped the same way the menu groups them.
    for (const dw of this.dynamicWidgets) {
      cmds.push({
        id: `dw-${dw.id}`,
        label: dw.title || dw.name,
        icon: dw.icon || 'widgets',
        category: dw.path.length > 1 ? dw.path[0] : 'Team Apps',
        run: () => this.launchDynamicWidget(dw),
        pinned: () => this.isDynamicPinned(dw.id),
        togglePin: (e) => this.toggleDynamicPin(dw, e),
      });
    }

    // Teams as places (the desktop model): a chat scoped to each team.
    // Starring one attaches it to the end of the toolbar rail.
    for (const team of this.visibleTeams) {
      const slug = team.slug || team.name;
      cmds.push({
        id: `chat-${slug}`,
        label: `Open workspace: ${team.name}`,
        icon: 'chat',
        category: 'Teams',
        run: () => this._widgetSvc.openChatForTeam$.next(slug),
        pinned: () => this.isPlacePinned(slug),
        togglePin: (e) => this.togglePlacePin(slug, e),
      });
    }

    // Remote places: teams on the caller's attached desktops, addressed as
    // {device}:{team}. Live-only rows — the fetch runs on palette open, and a
    // starred one persists on the rail (grey while the device is away).
    for (const device of this.remoteDevices) {
      if (!device.online) continue;
      for (const remoteTeam of this.remoteDeviceTeams.get(device.device_id) || []) {
        const address = `${device.device_id}:${remoteTeam.slug}`;
        cmds.push({
          id: `chat-${address}`,
          label: `Open workspace: ${remoteTeam.name} · ${device.name}`,
          icon: 'devices',
          category: 'Your Devices',
          run: () => this._widgetSvc.openChatForTeam$.next(address),
          pinned: () => this.isPlacePinned(address),
          togglePin: (e) => this.togglePlacePin(address, e),
        });
      }
    }

    // View toggles, mirroring the bottom of the Apps menu.
    cmds.push({
      id: 'toggle-theme',
      label: this.isDarkTheme ? 'Switch to Light Theme' : 'Switch to Dark Theme',
      icon: this.isDarkTheme ? 'light_mode' : 'dark_mode',
      category: 'View',
      run: () => this.toggleTheme(),
      pinned: () => this.isThemePinned,
      togglePin: (e) => this.toggleThemePin(e),
    });
    if (!this.isLayoutLocked) {
      cmds.push({
        id: 'toggle-layout',
        label: this.isDockviewMode ? 'Switch to Grid Layout' : 'Switch to Dock Layout',
        icon: this.isDockviewMode ? 'grid_view' : 'view_column',
        category: 'View',
        run: () => this.toggleLayoutEngine(),
        pinned: () => this.isLayoutPinned,
        togglePin: (e) => this.toggleLayoutPin(e),
      });
    }
    // Account section, mirroring the launcher's Account submenu
    // (minus the legacy team switch).
    if (!this.localMode) {
      cmds.push({
        id: 'license',
        label: 'License',
        icon: 'verified',
        category: 'Account',
        run: () => this.openWidget.emit('LicenseWidget'),
      });
      {
        // Flat, chat-style entries ("Agents: abodoo") — searchable, no
        // sub-selection (Ben, 2026-09-01: "more like open chat").
        const teams = (this.myTeams || []).map((t: any) => t.slug || t.name).filter(Boolean);
        for (const app of MenubarComponent.TEAM_APPS) {
          for (const t of teams) {
            cmds.push({
              id: `team-app:${app.name}:${t}`,
              label: `${app.title}: ${t}`,
              icon: app.icon,
              category: 'Team apps',
              run: () => this._widgetSvc.openTeamAppFor$.next({ name: app.name, team: t, title: app.title }),
            });
          }
        }
      }
      // Everyone gets the widget: admins to edit the policy, members for "what
      // applies to you" — where admin activity, transcript windows and holds on
      // them are shown (visible hands, docs/plans/org-policy.md).
      // One entry per team, like the team apps: admins edit, members see "what applies to me".
      const policyTeams = (this.myTeams || []).map((t: any) => t.slug || t.name).filter(Boolean);
      for (const t of (policyTeams.length ? policyTeams : [this.identity.me?.selectedTeamName || 'local'])) {
        cmds.push({
          id: `team-policy:${t}`,
          label: `Team Policy: ${t}`,
          icon: this.teamAdmin ? 'admin_panel_settings' : 'policy',
          category: 'Account',
          run: () => this.openWidgetForTeam.emit({ name: 'TeamPolicyWidget', team: t, title: 'Team Policy' }),
        });
        // Its sibling: Team Policy is what the organisation allows; Trusts is who I
        // let talk to my agents.
        cmds.push({
          id: `trusts:${t}`,
          label: `Trusts: ${t}`,
          icon: 'handshake',
          category: 'Account',
          run: () => this.openWidgetForTeam.emit({ name: 'TrustsWidget', team: t, title: 'Trusts' }),
        });
      }
      cmds.push({
        id: 'user-management',
        label: 'User Management',
        icon: 'manage_accounts',
        category: 'Account',
        run: () => this.openUserManager(),
      });
    }
    cmds.push({
      id: 'release-notes',
      label: `Release Notes (${this.version ?? '-'})`,
      icon: 'new_releases',
      category: 'Account',
      run: () => window.open(this.changelogURL, '_blank', 'noopener,noreferrer'),
    });
    if (!this.localMode) {
      cmds.push({
        id: 'logout',
        label: 'Logout',
        icon: 'exit_to_app',
        category: 'Account',
        run: () => this.logout(),
        pinned: () => this.isLogoutPinned,
        togglePin: (e) => this.toggleLogoutPin(e),
      });
    }

    return cmds;
  }

  // --- Starred places (team chats pinned to the toolbar rail) ---

  public pinnedPlaceSlugs: Set<string> = new Set();

  /** Starred places for the rail at the end of the toolbar: local teams in
   *  team order, then device-qualified remote places. A remote place whose
   *  device is away stays on the rail greyed out — stable geography, live-only
   *  made legible. */
  public get pinnedPlaces(): { name: string; slug: string; tooltip: string; offline: boolean }[] {
    const places: { name: string; slug: string; tooltip: string; offline: boolean }[] = [];
    for (const t of this.myTeams) {
      const slug = t.slug || t.name;
      if (this.pinnedPlaceSlugs.has(slug)) {
        places.push({ name: t.name, slug, tooltip: 'Workspace: ' + t.name, offline: false });
      }
    }
    for (const slug of this.pinnedPlaceSlugs) {
      const addr = parsePlaceAddress(slug);
      if (!addr) continue;
      const device = this.remoteDevices.find(d => d.device_id === addr.device);
      const online = !!device?.online;
      const deviceName = device?.name || addr.device;
      const teamName = this.remoteDeviceTeams.get(addr.device)?.find(t => t.slug === addr.team)?.name || addr.team;
      places.push({
        name: teamName,
        slug,
        tooltip: online ? `Workspace: ${teamName} · ${deviceName}` : `${deviceName} is offline`,
        offline: !online,
      });
    }
    return places;
  }

  public isPlacePinned(slug: string): boolean {
    return this.pinnedPlaceSlugs.has(slug);
  }

  public trackPlace(_index: number, place: { slug: string }): string {
    return place.slug;
  }

  /** Rail click. An offline-looking remote pin gets one self-heal attempt —
   *  the boot-time device fetch can race auth (or the laptop attached since),
   *  leaving stale grey. Genuinely offline stays inert. */
  public onPlaceClick(place: { slug: string; offline: boolean }): void {
    if (!place.offline) {
      this._widgetSvc.openChatForTeam$.next(place.slug);
      return;
    }
    void this.refreshRemotePlaces().then(() => {
      const addr = parsePlaceAddress(place.slug);
      const device = addr ? this.remoteDevices.find(d => d.device_id === addr.device) : null;
      if (device?.online) this._widgetSvc.openChatForTeam$.next(place.slug);
    });
  }

  public togglePlacePin(slug: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.pinnedPlaceSlugs.has(slug)) {
      this.pinnedPlaceSlugs.delete(slug);
    } else {
      this.pinnedPlaceSlugs.add(slug);
    }
    this.savePinnedPlaces();
    this._cdr.detectChanges();
  }

  private loadPinnedPlaces(): void {
    try {
      const stored = localStorage.getItem(PINNED_PLACES_KEY);
      if (stored) {
        this.pinnedPlaceSlugs = new Set(JSON.parse(stored) as string[]);
      }
    } catch (e) {
      console.warn('Failed to load pinned places:', e);
    }
  }

  private savePinnedPlaces(): void {
    try {
      localStorage.setItem(PINNED_PLACES_KEY, JSON.stringify(Array.from(this.pinnedPlaceSlugs)));
    } catch (e) {
      console.warn('Failed to save pinned places:', e);
    }
  }

  // --- Logout pin ---

  public isLogoutPinned = false;

  public toggleLogoutPin(event: MouseEvent): void {
    event.stopPropagation();
    this.isLogoutPinned = !this.isLogoutPinned;
    localStorage.setItem(PINNED_LOGOUT_KEY, String(this.isLogoutPinned));
    this._cdr.detectChanges();
  }

  public updatePaletteFilter(): void {
    const q = this.paletteQuery.trim().toLowerCase();
    const matched = !q
      ? this._paletteCommands
      : this._paletteCommands.filter(c =>
          c.label.toLowerCase().includes(q) || c.category.toLowerCase().includes(q));

    this._paletteFlat = matched.map((c, i) => ({ ...c, flatIndex: i }));
    const groups = new Map<string, PaletteRow[]>();
    for (const row of this._paletteFlat) {
      if (!groups.has(row.category)) {
        groups.set(row.category, []);
      }
      groups.get(row.category)!.push(row);
    }
    this.paletteGroups = Array.from(groups.entries()).map(([category, commands]) => ({ category, commands }));
    this.paletteIndex = 0;
    this._cdr.detectChanges();
  }

  public onPaletteKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePalette();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this._paletteFlat.length) {
        this.setPaletteIndex((this.paletteIndex + 1) % this._paletteFlat.length);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this._paletteFlat.length) {
        this.setPaletteIndex((this.paletteIndex - 1 + this._paletteFlat.length) % this._paletteFlat.length);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const cmd = this._paletteFlat[this.paletteIndex];
      if (cmd) {
        this.runPaletteCommand(cmd);
      }
    }
  }

  public runPaletteCommand(cmd: PaletteCommand): void {
    if (!cmd.keepOpen) {
      this.closePalette();
    }
    cmd.run();
  }
}
